import { useState, useRef, useEffect, ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { X, Send, Clock, CheckCircle2, RotateCcw, Paperclip, ThumbsUp, ThumbsDown, Plus, Square, CheckSquare, Archive, ArchiveRestore, Trash2, Pencil, GripVertical, Copy, History, ChevronDown } from "lucide-react";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import { NovaTarefaModal } from "./NovaTarefaModal";
import { confirmarDuasVezes } from "../../utils/confirmarDuasVezes";
import {
  buscarTarefa,
  adicionarFeedback,
  editarFeedback,
  Feedback,
  uploadAnexo,
  anexoUrlAbsoluto,
  aprovarConclusao,
  criarSubitem,
  atualizarSubitem,
  deletarSubitem,
  atualizarTarefa,
  arquivarTarefa,
  desarquivarTarefa,
  aprovarArquivamentoTarefa,
  rejeitarArquivamentoTarefa,
  deletarTarefa,
  duplicarTarefa,
  Subitem,
  listarTempoAjustes,
  ajustarTempo,
  aprovarTempoAjuste,
  rejeitarTempoAjuste,
  TempoPessoa,
  listarHistoricoTarefa,
  EventoHistorico,
  moverTarefa,
} from "../../services/tarefas";
import { listarColunasKanban } from "../../services/colunasKanban";
import { listarUsuariosAtivos, UsuarioAtivo } from "../../services/usuarios";
import { useAuth } from "../../hooks/useAuth";
import { urgenciaTarefa, STATUS_LABELS } from "../../utils/urgencia";
import { MentionTextarea } from "../../components/MentionTextarea";
import { AudioRecorder } from "../../components/AudioRecorder";
import { AnexoView } from "../../components/AnexoView";
import { useConfirm } from "../../components/ConfirmProvider";
import { renderTextoRico } from "../../utils/textoComLinks";
import { getMe, definirTarefaAtual } from "../../services/me";
import { listarAjustesTarefa, registrarAjusteComAnexos, deletarAjuste, resolverAjuste, editarAjuste, listarMotivosAjuste, TarefaAjuste } from "../../services/ajustes";
import { RegistrarAjusteModal, DadosAjuste } from "../../components/RegistrarAjusteModal";
import { useFecharComEsc } from "../../hooks/useFecharComEsc";
import { useRascunhoCampo } from "../../hooks/useRascunhoCampo";
import { Focus, Wrench, Package } from "lucide-react";
import { listarEntregasCard, registrarEntrega, deletarEntrega } from "../../services/entregas";
import { BriefingSecao } from "../../components/BriefingSecao";
import { Avatar } from "../../components/Avatar";
import { ProjetoAvatar } from "../../components/ProjetoAvatar";

interface TarefaDrawerProps {
  tarefaId: string;
  onClose: () => void;
  /** Chamado após duplicar — recebe o id da cópia (parent pode abrir o drawer dela). */
  onDuplicada?: (novaId: string) => void;
}

function tempoDesde(iso: string | null | undefined): string {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return "agora";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}min`;
}

/** Link externo seguro: só vira <a> se for http/https (anti-XSS); senão mostra texto. */
function LinkExterno({ url, children }: { url: string; children: ReactNode }) {
  if (/^https?:\/\//i.test(url)) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline inline-flex items-center gap-1">
        {children}
      </a>
    );
  }
  return <span className="inline-flex items-center gap-1 text-slate-400">{children}</span>;
}

function fmtHoras(ms: number): string {
  const totalMin = Math.round(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m}min`;
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, "0")}`;
}

/**
 * CARDS 4 — Programado × Executado. Compara o esforço estimado (horasEstimadas)
 * com o tempo realmente trabalhado (soma das sessões úteis). Só aparece se houver
 * estimativa OU tempo executado.
 */
function ProgramadoExecutado({ horasEstimadas, tempoExecutadoMs }: { horasEstimadas?: number | null; tempoExecutadoMs?: number }) {
  const execMs = tempoExecutadoMs ?? 0;
  const progMs = horasEstimadas && horasEstimadas > 0 ? horasEstimadas * 3600000 : 0;
  if (!progMs && !execMs) return null;

  const pct = progMs > 0 ? (execMs / progMs) * 100 : 0;
  const estourou = progMs > 0 && execMs > progMs;
  const corBarra = estourou ? "bg-red-500" : "bg-emerald-500";

  return (
    <div className="mt-2 bg-slate-50 border border-slate-200 rounded-lg p-2.5">
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="font-medium text-slate-600 flex items-center gap-1"><Clock size={12} /> Programado × Executado</span>
        <span className="text-slate-500 tabular-nums">
          {fmtHoras(execMs)}{progMs > 0 && <> / {fmtHoras(progMs)}</>}
          {progMs > 0 && <span className={`ml-1 font-semibold ${estourou ? "text-red-600" : "text-emerald-600"}`}>({Math.round(pct)}%)</span>}
        </span>
      </div>
      {progMs > 0 ? (
        <div className="bg-slate-200 rounded-full h-2 overflow-hidden">
          <div className={`h-full rounded-full transition-all ${corBarra}`} style={{ width: `${Math.min(100, pct)}%` }} />
        </div>
      ) : (
        <p className="text-[11px] text-slate-400">Sem estimativa de esforço — só o tempo executado é exibido.</p>
      )}
      {estourou && <p className="text-[11px] text-red-500 mt-1">Estourou o esforço estimado em {fmtHoras(execMs - progMs)}.</p>}
    </div>
  );
}

/** Tempo trabalhado por pessoa na tarefa + correção manual (a própria pessoa ou
 * gestor/admin) com histórico auditável de cada ajuste. */
const STATUS_AJUSTE: Record<string, { txt: string; cls: string }> = {
  pendente: { txt: "pendente", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
  aprovado: { txt: "aprovado", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" },
  rejeitado: { txt: "rejeitado", cls: "bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400" },
};

function TempoTrabalhado({ tarefaId, tempoPorPessoa, meId, ehGestor }: {
  tarefaId: string; tempoPorPessoa: TempoPessoa[]; meId?: string; ehGestor: boolean;
}) {
  const qc = useQueryClient();
  const [ajustando, setAjustando] = useState<TempoPessoa | null>(null);
  const { data: correcoes = [] } = useQuery({
    queryKey: ["tempo-ajustes", tarefaId],
    queryFn: () => listarTempoAjustes(tarefaId),
  });

  function invalidarAjustes() {
    qc.invalidateQueries({ queryKey: ["tempo-ajustes", tarefaId] });
    qc.invalidateQueries({ queryKey: ["tarefa", tarefaId] });
    qc.invalidateQueries({ queryKey: ["tarefas"] });
    qc.invalidateQueries({ queryKey: ["tempo-ajustes-pendentes"] });
  }
  const aprovarM = useMutation({
    mutationFn: (id: string) => aprovarTempoAjuste(id),
    onSuccess: invalidarAjustes,
    onError: (e: any) => alert(e?.response?.data?.error?.message || "Não foi possível aprovar."),
  });
  const rejeitarM = useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo?: string }) => rejeitarTempoAjuste(id, motivo),
    onSuccess: invalidarAjustes,
    onError: (e: any) => alert(e?.response?.data?.error?.message || "Não foi possível rejeitar."),
  });

  if (tempoPorPessoa.length === 0 && correcoes.length === 0) return null;

  return (
    <div className="mt-2 bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-lg p-2.5">
      <div className="text-xs font-medium text-slate-600 dark:text-slate-300 flex items-center gap-1 mb-1.5">
        <Clock size={12} /> Tempo por pessoa
      </div>
      <ul className="space-y-1">
        {tempoPorPessoa.map((p) => (
          <li key={p.usuario.id} className="flex items-center gap-2 text-xs">
            <span className="text-slate-600 dark:text-slate-300 truncate">{p.usuario.nome}</span>
            <span className="ml-auto tabular-nums text-slate-500">{fmtHoras(p.ms)}</span>
            {(p.usuario.id === meId || ehGestor) && (
              <button type="button" onClick={() => setAjustando(p)} className="text-blue-600 hover:underline shrink-0" title="Corrigir tempo">
                ajustar
              </button>
            )}
          </li>
        ))}
      </ul>

      {correcoes.length > 0 && (
        <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700">
          <div className="text-[11px] font-medium text-slate-500 mb-1">Correções de tempo</div>
          <ul className="space-y-1.5">
            {correcoes.map((c) => {
              const badge = STATUS_AJUSTE[c.status] || STATUS_AJUSTE.aprovado;
              const verbo = c.status === "pendente" ? "quer corrigir" : c.status === "rejeitado" ? "tentou corrigir" : "corrigiu";
              return (
                <li key={c.id} className={`text-[11px] leading-snug ${c.status === "rejeitado" ? "text-slate-400" : "text-slate-500"}`}>
                  <span className={`inline-block px-1.5 py-px rounded text-[9px] font-semibold uppercase mr-1 align-middle ${badge.cls}`}>{badge.txt}</span>
                  <span className="font-medium text-slate-600 dark:text-slate-300">{c.autor?.nome || "—"}</span>
                  {" "}{verbo}{" o tempo de "}
                  <span className="font-medium">{c.usuario?.nome || "—"}</span>
                  {": "}{fmtHoras(c.totalAnteriorMs)} → {fmtHoras(c.totalNovoMs)}
                  <span className="text-slate-400"> ({c.deltaMs >= 0 ? "+" : "−"}{fmtHoras(Math.abs(c.deltaMs))})</span>
                  {" · "}<span className="italic">{c.motivo}</span>
                  {" · "}<span className="text-slate-400">{new Date(c.criadoEm).toLocaleString("pt-BR")}</span>
                  {c.status === "aprovado" && c.aprovador && <span className="text-emerald-600 dark:text-emerald-400"> · ✓ {c.aprovador.nome}</span>}
                  {c.status === "rejeitado" && c.decisaoMotivo && <span className="text-slate-400"> · motivo: {c.decisaoMotivo}</span>}
                  {c.status === "pendente" && ehGestor && (
                    <span className="inline-flex gap-1.5 ml-1.5 align-middle">
                      <button
                        type="button"
                        disabled={aprovarM.isPending || rejeitarM.isPending}
                        onClick={() => aprovarM.mutate(c.id)}
                        className="text-emerald-600 hover:underline font-medium disabled:opacity-50"
                      >
                        aprovar
                      </button>
                      <button
                        type="button"
                        disabled={aprovarM.isPending || rejeitarM.isPending}
                        onClick={() => { const m = window.prompt("Motivo da rejeição (opcional):") ?? undefined; rejeitarM.mutate({ id: c.id, motivo: m }); }}
                        className="text-red-500 hover:underline font-medium disabled:opacity-50"
                      >
                        rejeitar
                      </button>
                    </span>
                  )}
                  {c.status === "pendente" && !ehGestor && <span className="text-amber-600 dark:text-amber-400"> · aguardando gestor</span>}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {ajustando && (
        <AjustarTempoModal
          tarefaId={tarefaId}
          pessoa={ajustando}
          onFechar={() => setAjustando(null)}
          onSalvo={() => {
            setAjustando(null);
            qc.invalidateQueries({ queryKey: ["tarefa", tarefaId] });
            qc.invalidateQueries({ queryKey: ["tempo-ajustes", tarefaId] });
            qc.invalidateQueries({ queryKey: ["tarefas"] });
          }}
        />
      )}
    </div>
  );
}

function AjustarTempoModal({ tarefaId, pessoa, onFechar, onSalvo }: {
  tarefaId: string; pessoa: TempoPessoa; onFechar: () => void; onSalvo: () => void;
}) {
  const atualMin = Math.round(pessoa.ms / 60000);
  const [horas, setHoras] = useState(String(Math.floor(atualMin / 60)));
  const [min, setMin] = useState(String(atualMin % 60));
  const [motivo, setMotivo] = useState("");
  const [salvando, setSalvando] = useState(false);

  useFecharComEsc(onFechar, () => motivo.trim().length > 0);

  // Prévia ao vivo do efeito. A Thainá perguntou "quando é pra somar e quando é
  // pra diminuir?" — dois campos Horas/Minutos leem como "quanto acrescentar",
  // mas o valor é o TOTAL final. Sem ver o resultado antes de salvar, digitar
  // "0h30" pra somar 30min em cima de 1h30 na verdade TIRA 1h. Então a tela
  // passou a dizer, em texto, o que vai acontecer.
  const novoMin = (parseInt(horas) || 0) * 60 + (parseInt(min) || 0);
  const deltaMin = novoMin - atualMin;
  const semMudanca = deltaMin === 0;

  async function salvar() {
    if (!motivo.trim()) { alert("Informe o motivo da correção."); return; }
    const totalMinutos = novoMin;
    setSalvando(true);
    try {
      const reg = await ajustarTempo(tarefaId, { usuarioId: pessoa.usuario.id, totalMinutos, motivo: motivo.trim() });
      if (reg?.status === "pendente") {
        alert("Ajuste enviado para aprovação do gestor. O tempo só muda depois que ele aprovar.");
      }
      onSalvo();
    } catch (e: any) {
      alert(e?.response?.data?.error?.message || "Não foi possível ajustar o tempo.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    // Clicar no fundo NÃO fecha: perdia o motivo digitado (regra da Rec, jul/2026).
    <div className="fixed inset-0 z-[75] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-slate-900 rounded-xl w-full max-w-sm shadow-2xl">
        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800 dark:text-slate-100 text-sm">Corrigir tempo — {pessoa.usuario.nome}</h3>
          <button onClick={onFechar} className="p-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white shadow-sm" title="Fechar sem salvar"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-slate-500">
            Tempo atual: <span className="font-medium">{fmtHoras(pessoa.ms)}</span>. Informe o tempo <b>total correto</b> que a pessoa trabalhou nesta tarefa — <b>não</b> o quanto quer somar ou tirar. O sistema calcula a diferença sozinho.
          </p>
          <div>
            <label className="text-xs font-medium text-slate-700 dark:text-slate-200 block mb-1">
              Tempo total correto
            </label>
            <div className="flex items-end gap-2">
              <div>
                <input type="number" min={0} value={horas} onChange={(e) => setHoras(e.target.value)} className="w-20 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none focus:border-blue-500" />
                <span className="block text-[10px] text-slate-400 mt-0.5 text-center">horas</span>
              </div>
              <div>
                <input type="number" min={0} max={59} value={min} onChange={(e) => setMin(e.target.value)} className="w-20 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none focus:border-blue-500" />
                <span className="block text-[10px] text-slate-400 mt-0.5 text-center">minutos</span>
              </div>
            </div>
          </div>

          {/* Resultado em texto ANTES de salvar — é isso que tira a dúvida do
              "somar ou diminuir": a pessoa lê o efeito, não deduz. */}
          <div
            className={
              "rounded-lg px-3 py-2 text-xs border " +
              (semMudanca
                ? "bg-slate-50 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700 text-slate-500"
                : deltaMin > 0
                ? "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300"
                : "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300")
            }
          >
            {semMudanca ? (
              <>Igual ao tempo atual — nada a corrigir.</>
            ) : (
              <>
                {fmtHoras(pessoa.ms)} → <b>{fmtHoras(novoMin * 60000)}</b>
                <br />
                Isso vai <b>{deltaMin > 0 ? "somar" : "tirar"} {fmtHoras(Math.abs(deltaMin) * 60000)}</b>
                {deltaMin > 0 ? " ao tempo" : " do tempo"} de {pessoa.usuario.nome.split(" ")[0]}.
              </>
            )}
          </div>
          <div>
            <label className="text-xs text-slate-600 dark:text-slate-300 block mb-1">Motivo <span className="text-red-400">*</span></label>
            <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2} placeholder="Ex.: esqueci de pausar o play" className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none focus:border-blue-500" />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
          <button onClick={onFechar} className="px-3 py-1.5 rounded text-sm bg-slate-100 dark:bg-slate-800 hover:bg-slate-200">Cancelar</button>
          {/* Bloqueia o salvar sem mudança: o backend já recusa, mas é melhor a
              pessoa ver na tela do que levar um alert depois de escrever o motivo. */}
          <button onClick={salvar} disabled={salvando || semMudanca} title={semMudanca ? "O tempo informado é igual ao atual" : undefined} className="px-4 py-1.5 rounded text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed">{salvando ? "Salvando…" : "Salvar correção"}</button>
        </div>
      </div>
    </div>
  );
}

// ===== Edição inline (Cadastro #8 / Viz #6) =====

/** Prazo clicável → vira <input date> e autosava ao escolher. */
function EditavelPrazo({ prazoISO, onSalvar, podeEditar }: { prazoISO: string; onSalvar: (iso: string) => void; podeEditar: boolean }) {
  const [editando, setEditando] = useState(false);
  const d = new Date(prazoISO);
  const ymd = isNaN(d.getTime()) ? "" : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  if (editando && podeEditar) {
    return (
      <input
        type="date"
        autoFocus
        defaultValue={ymd}
        onChange={(e) => { if (e.target.value) { onSalvar(`${e.target.value}T12:00:00`); } setEditando(false); }}
        onBlur={() => setEditando(false)}
        className="text-xs px-1.5 py-0.5 rounded border border-blue-300 outline-none"
      />
    );
  }
  return (
    <strong
      onClick={() => podeEditar && setEditando(true)}
      className={podeEditar ? "cursor-pointer hover:text-blue-600 decoration-dotted underline-offset-2 hover:underline" : ""}
      title={podeEditar ? "Clique para alterar o prazo" : undefined}
    >
      {isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR")}
    </strong>
  );
}

/** Responsável clicável → vira <select> e autosava ao escolher. */
function EditavelAtribuido({ atual, usuarios, onSalvar, podeEditar }: {
  atual?: { id: string; nome: string } | null;
  usuarios: Array<{ id: string; nome: string }>;
  onSalvar: (id: string) => void;
  podeEditar: boolean;
}) {
  const [editando, setEditando] = useState(false);
  if (editando && podeEditar) {
    return (
      <select
        autoFocus
        defaultValue={atual?.id || ""}
        onChange={(e) => { if (e.target.value) onSalvar(e.target.value); setEditando(false); }}
        onBlur={() => setEditando(false)}
        className="text-xs px-1.5 py-0.5 rounded border border-blue-300 outline-none max-w-[180px]"
      >
        {usuarios.map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
      </select>
    );
  }
  return (
    <strong
      onClick={() => podeEditar && setEditando(true)}
      className={podeEditar ? "cursor-pointer hover:text-blue-600 decoration-dotted underline-offset-2 hover:underline" : ""}
      title={podeEditar ? "Clique para reatribuir" : undefined}
    >
      {atual?.nome || "—"}
    </strong>
  );
}

/** Descrição clicável → textarea com autosave (debounce + blur) + colar imagem inline. */
function EditavelDescricao({ valor, onSalvar, podeEditar, onUploadImagem, usuarios }: {
  valor: string | null;
  onSalvar: (v: string) => Promise<unknown>;
  podeEditar: boolean;
  onUploadImagem?: (file: File) => Promise<string>;
  /** Pro editor rico (menção + formatação), igual aos comentários. */
  usuarios: UsuarioAtivo[];
}) {
  const [editando, setEditando] = useState(false);
  const [txt, setTxt] = useState(valor || "");
  const [estado, setEstado] = useState<"idle" | "salvando" | "salvo" | "erro" | "enviando-imagem">("idle");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ultimoSalvo = useRef(valor || "");

  // NÃO re-sincroniza enquanto está editando — senão um refetch (após autosave)
  // sobrescreve o texto que a pessoa ainda está digitando.
  useEffect(() => {
    if (editando) return;
    setTxt(valor || "");
    ultimoSalvo.current = valor || "";
  }, [valor, editando]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  async function salvarAgora(v: string) {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (v === ultimoSalvo.current) { setEstado("idle"); return; }
    setEstado("salvando");
    try {
      await onSalvar(v);
      ultimoSalvo.current = v; // só avança após sucesso → permite re-tentar o mesmo texto
      setEstado("salvo");
    } catch {
      setEstado("erro");
    }
  }
  function aoDigitar(v: string) {
    setTxt(v);
    setEstado("salvando");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { void salvarAgora(v); }, 1000);
  }

  if (editando && podeEditar) {
    return (
      // data-editor-descricao: marca o editor da DESCRIÇÃO pra o handler global de
      // paste do drawer NÃO sequestrar a imagem pro comentário (bug da Thainá,
      // jul/2026): colar imagem aqui deve inserir INLINE na descrição.
      <div className="mt-2" data-editor-descricao>
        {/* Editor rico (jul/2026, melhoria 6): a descrição virou o MESMO editor
            dos comentários — negrito/cor/listas/checklist SEM mostrar código +
            colar imagem. Antes era um textarea cru que exibia os códigos. */}
        <MentionTextarea
          value={txt}
          onChange={aoDigitar}
          usuarios={usuarios}
          toolbar
          rows={4}
          placeholder="Descreva a tarefa… (formatação na barra; cole imagem com Ctrl+V)"
          onUploadImagem={onUploadImagem}
        />
        <div className="flex items-center gap-2 mt-1">
          <button
            type="button"
            onClick={() => { void salvarAgora(txt); setEditando(false); }}
            className="text-xs px-2.5 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
          >
            Concluir
          </button>
          <span className="text-[10px]">
            {estado === "enviando-imagem" ? <span className="text-blue-500">enviando imagem…</span>
              : estado === "salvando" ? <span className="text-slate-400">salvando…</span>
              : estado === "salvo" ? <span className="text-emerald-500">salvo ✓</span>
              : estado === "erro" ? <span className="text-red-500">erro ao salvar — tente de novo</span>
              : <span className="text-slate-400">salva automático</span>}
          </span>
        </div>
      </div>
    );
  }
  return (
    <div className="mt-2">
      {valor ? (
        <p
          onClick={() => podeEditar && setEditando(true)}
          className={`text-sm text-slate-700 whitespace-pre-wrap break-words ${podeEditar ? "cursor-text hover:bg-slate-50 rounded -mx-1 px-1" : ""}`}
          title={podeEditar ? "Clique para editar" : undefined}
        >
          {renderTextoRico(valor, "text-blue-600 hover:text-blue-700 underline")}
        </p>
      ) : podeEditar ? (
        <button onClick={() => setEditando(true)} className="text-sm text-slate-400 italic hover:text-slate-600">
          + Adicionar descrição…
        </button>
      ) : null}
    </div>
  );
}

// ===== Comentário/feedback no histórico (Comentários #1, #2, #3) =====
const FB_BADGE: Record<string, { label: string; cls: string }> = {
  comentario: { label: "comentou", cls: "bg-blue-100 text-blue-700" },
  adiamento: { label: "adiou", cls: "bg-amber-100 text-amber-700" },
  conclusao: { label: "concluiu", cls: "bg-emerald-100 text-emerald-700" },
  reabertura: { label: "reabriu", cls: "bg-slate-100 text-slate-600" },
};
const LIMITE_COLAPSO = 320;

function FeedbackItem({ f, tarefaId, meId, onSalvar, salvando, usuarios }: {
  f: Feedback;
  tarefaId: string;
  meId?: string;
  onSalvar: (texto: string) => void;
  salvando: boolean;
  /** Pro editor rico da edição (menção + formatação), igual ao de escrever. */
  usuarios: UsuarioAtivo[];
}) {
  const [expandido, setExpandido] = useState(false);
  const [editando, setEditando] = useState(false);
  const [txt, setTxt] = useState(f.texto || "");
  const badge = FB_BADGE[f.tipo] || { label: f.tipo, cls: "bg-slate-100 text-slate-600" };
  const podeEditar = f.tipo === "comentario" && f.autor.id === meId;
  const ehMeu = f.autor.id === meId;
  const textoLongo = (f.texto?.length || 0) > LIMITE_COLAPSO;

  function commit() {
    const limpo = txt.trim();
    if (limpo && limpo !== (f.texto || "")) onSalvar(limpo);
    setEditando(false);
  }

  return (
    <div className={`rounded-lg border p-2.5 shadow-sm ${ehMeu ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800" : "bg-white dark:bg-slate-800/60 border-slate-200 dark:border-slate-700"}`}>
      <div className="flex items-center gap-2 text-xs">
        <Avatar nome={f.autor.nome} tamanho={22} />
        <span className="font-semibold text-slate-700 dark:text-slate-200">{f.autor.nome}</span>
        <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase font-medium ${badge.cls}`}>{badge.label}</span>
        <span className="text-slate-400">{new Date(f.criadoEm).toLocaleString("pt-BR")}</span>
        {f.editadoEm && <span className="text-slate-400 italic">· editado</span>}
        {podeEditar && !editando && (
          <button
            onClick={() => { setTxt(f.texto || ""); setEditando(true); }}
            className="ml-auto text-slate-400 hover:text-blue-600"
            title="Editar comentário"
          >
            <Pencil size={12} />
          </button>
        )}
      </div>

      {editando ? (
        <div className="mt-2">
          {/* Mesmo editor rico de escrever (jul/2026). Antes era um textarea
              cru: escrever escondia os códigos e EDITAR mostrava
              `{vermelho}**x**{/vermelho}` na cara da pessoa — a inconsistência
              seria pior que o problema original. */}
          <MentionTextarea
            value={txt}
            onChange={setTxt}
            usuarios={usuarios}
            rows={3}
            toolbar
            className="w-full text-sm rounded-lg border border-blue-300 p-2 outline-none focus:border-blue-500"
          />
          <div className="flex items-center gap-2 mt-1">
            <button onClick={commit} disabled={salvando || !txt.trim()} className="text-xs px-2.5 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
              Salvar
            </button>
            <button onClick={() => setEditando(false)} className="text-xs px-2.5 py-1 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200">
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <>
          {f.texto && (
            <div className={`text-sm text-slate-700 dark:text-slate-200 mt-1.5 whitespace-pre-wrap break-words ${textoLongo && !expandido ? "line-clamp-6" : ""}`}>
              {renderTextoRico(f.texto, "text-blue-600 hover:text-blue-700 underline")}
            </div>
          )}
          {textoLongo && (
            <button onClick={() => setExpandido((v) => !v)} className="text-xs text-blue-600 hover:underline mt-0.5">
              {expandido ? "ver menos" : "ver mais"}
            </button>
          )}
        </>
      )}

      {f.anexos && f.anexos.length > 0 && (
        <div className="mt-2 space-y-2">
          {f.anexos.map((a) => (
            <AnexoView key={a.id} anexo={a} tarefaId={tarefaId} />
          ))}
        </div>
      )}
      {f.tipo === "adiamento" && f.novoPrazo && (
        <p className="text-xs text-amber-700 mt-1">
          Novo prazo: {new Date(f.novoPrazo).toLocaleDateString("pt-BR")}
        </p>
      )}
    </div>
  );
}

// MELHORIA 25 — entregas realizadas do card (ledger). Botão "Realizado" soma
// entregas ao longo do tempo (qtd + dia), independente de mover pra Finalizado.
// Alimenta o relatório "Entregas realizadas" (por dia/pessoa/categoria/cliente).
function EntregasCard({ tarefaId, meId, ehGestor }: { tarefaId: string; meId?: string; ehGestor: boolean }) {
  const qc = useQueryClient();
  const hoje = (() => { const d = new Date(); const p = (n: number) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`; })();
  const [aberto, setAberto] = useState(false);
  const [qtd, setQtd] = useState("1");
  const [data, setData] = useState(hoje);
  const [obs, setObs] = useState("");
  const { data: entregas = [] } = useQuery({ queryKey: ["entregas", tarefaId], queryFn: () => listarEntregasCard(tarefaId) });
  const registrarM = useMutation({
    mutationFn: () => registrarEntrega(tarefaId, { quantidade: Math.max(1, Math.trunc(Number(qtd) || 1)), data, observacao: obs.trim() || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["entregas", tarefaId] });
      qc.invalidateQueries({ queryKey: ["tarefa", tarefaId] });
      setQtd("1"); setData(hoje); setObs(""); setAberto(false);
    },
  });
  const delM = useMutation({
    mutationFn: (id: string) => deletarEntrega(tarefaId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["entregas", tarefaId] }),
  });
  const total = entregas.reduce((s, e) => s + e.quantidade, 0);

  return (
    <div className="mt-2 bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-lg p-2.5">
      <div className="text-xs font-medium text-slate-600 dark:text-slate-300 flex items-center gap-1 mb-1.5">
        <Package size={12} /> Entregas realizadas
        {total > 0 && <span className="ml-1 text-[10px] font-bold text-emerald-700 bg-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-300 px-1.5 py-px rounded-full">{total} {total === 1 ? "peça" : "peças"}</span>}
        <button type="button" onClick={() => setAberto((v) => !v)} className="ml-auto text-emerald-600 hover:underline font-medium shrink-0 inline-flex items-center gap-0.5">
          <Plus size={12} /> Realizado
        </button>
      </div>

      {aberto && (
        <div className="flex flex-wrap items-end gap-2 mb-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-2">
          <label className="text-[11px] text-slate-500">
            Peças
            <input type="number" min={1} value={qtd} onChange={(e) => setQtd(e.target.value)} className="block w-16 mt-0.5 px-2 py-1 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-900 text-sm outline-none" />
          </label>
          <label className="text-[11px] text-slate-500">
            Dia
            <input type="date" value={data} onChange={(e) => setData(e.target.value)} className="block mt-0.5 px-2 py-1 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-900 text-sm outline-none" />
          </label>
          <label className="text-[11px] text-slate-500 flex-1 min-w-[120px]">
            Observação (opcional)
            <input value={obs} onChange={(e) => setObs(e.target.value)} placeholder="ex.: arte final" className="block w-full mt-0.5 px-2 py-1 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-900 text-sm outline-none" />
          </label>
          <button type="button" disabled={registrarM.isPending} onClick={() => registrarM.mutate()} className="px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
            {registrarM.isPending ? "…" : "Registrar"}
          </button>
        </div>
      )}

      {entregas.length === 0 ? (
        <p className="text-[11px] text-slate-400">Nenhuma entrega registrada ainda.</p>
      ) : (
        <ul className="space-y-1">
          {entregas.map((e) => (
            <li key={e.id} className="flex items-center gap-2 text-xs">
              <span className="font-semibold text-emerald-700 dark:text-emerald-400 tabular-nums shrink-0">{e.quantidade}×</span>
              <span className="text-slate-500 shrink-0">{new Date(e.data).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}</span>
              <span className="text-slate-600 dark:text-slate-300 truncate">
                {e.usuario.nome}
                {e.observacao && <span className="text-slate-400 italic"> · {e.observacao}</span>}
              </span>
              {(e.usuario.id === meId || ehGestor) && (
                <button type="button" onClick={() => delM.mutate(e.id)} disabled={delM.isPending} className="ml-auto text-slate-400 hover:text-red-500 shrink-0" title="Remover entrega">
                  <Trash2 size={12} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function TarefaDrawer({ tarefaId, onClose, onDuplicada }: TarefaDrawerProps) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [texto, setTexto] = useState("");
  const [novoSubitem, setNovoSubitem] = useState("");
  const [novoPrazo, setNovoPrazo] = useState("");
  const [showAdiar, setShowAdiar] = useState(false);
  // Anexos "preparados" no compositor (colados/escolhidos) — enviados junto com
  // o comentário, estilo WhatsApp (imagem + legenda no mesmo envio).
  const [anexosPendentes, setAnexosPendentes] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Aba de atividade (histórico do card) — sobrepõe o conteúdo do drawer.
  const [showAtividade, setShowAtividade] = useState(false);

  // ESC: primeiro volta da aba de atividade; só então fecha o card. Se tiver
  // comentário/anexo em andamento, confirma antes de descartar.
  useFecharComEsc(
    () => { if (showAtividade) setShowAtividade(false); else onClose(); },
    () => !showAtividade && (texto.trim().length > 0 || anexosPendentes.length > 0),
  );

  // Melhoria 11 — rascunho do comentário: se fechar sem querer (X, ESC, refresh,
  // queda do backend) o texto volta ao reabrir o card. Enviar limpa (setTexto("")).
  useRascunhoCampo(`rascunho_comentario_${tarefaId}`, texto, setTexto);

  const { data: tarefa, isLoading } = useQuery({
    queryKey: ["tarefa", tarefaId],
    queryFn: () => buscarTarefa(tarefaId),
  });

  const { data: usuarios = [] } = useQuery({ queryKey: ["usuarios-ativos"], queryFn: listarUsuariosAtivos });
  // MELHORIA 17 — mover o card de coluna com ele ABERTO (seletor tipo Trello).
  const { data: colunas = [] } = useQuery({ queryKey: ["colunas-kanban"], queryFn: () => listarColunasKanban() });

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["tarefa", tarefaId] });
    qc.invalidateQueries({ queryKey: ["tarefas"] });
  }

  const feedbackM = useMutation({
    mutationFn: async (payload: any) => {
      const fb: any = await adicionarFeedback(tarefaId, payload);
      // Anexa os arquivos preparados a ESTE feedback (imagem + legenda juntos).
      if (fb?.id && anexosPendentes.length > 0) {
        for (const f of anexosPendentes) {
          try { await uploadAnexo(tarefaId, f, { feedbackId: fb.id }); } catch { /* best-effort */ }
        }
      }
      return fb;
    },
    onSuccess: () => {
      setTexto("");
      setNovoPrazo("");
      setShowAdiar(false);
      setAnexosPendentes([]);
      invalidate();
    },
  });

  const uploadM = useMutation({
    mutationFn: (file: File | Blob) => uploadAnexo(tarefaId, file),
    onSuccess: () => invalidate(),
  });

  // Ctrl+V com imagem no clipboard → prepara o anexo no compositor (não envia na
  // hora): aparece o preview e a pessoa digita a legenda antes de mandar.
  useEffect(() => {
    if (!tarefa) return;
    function onPaste(e: ClipboardEvent) {
      // Se o paste está DENTRO do editor da descrição, o próprio MentionTextarea
      // (aoColar) insere a imagem INLINE — não sequestrar pro anexo do comentário.
      // (Bug da Thainá: imagem colada na descrição caía no feedback.)
      const foco = document.activeElement as HTMLElement | null;
      if (foco && foco.closest && foco.closest("[data-editor-descricao]")) return;
      const dt = e.clipboardData;
      if (!dt) return;
      const imgs: File[] = [];
      for (const it of Array.from(dt.items || [])) {
        if (it.kind === "file") {
          const f = it.getAsFile();
          if (f && /^(image|audio|video)\//.test(f.type)) imgs.push(f);
        }
      }
      if (imgs.length === 0 && dt.files?.length) {
        for (const f of Array.from(dt.files)) if (/^(image|audio|video)\//.test(f.type)) imgs.push(f);
      }
      if (imgs.length === 0) return; // texto normal segue o fluxo padrão
      e.preventDefault();
      const prep = imgs.map((f) =>
        !f.name || f.name === "image.png" || f.name === "blob"
          ? new File([f], `colado-${Date.now()}.${f.type.split("/")[1] || "png"}`, { type: f.type })
          : f,
      );
      setAnexosPendentes((prev) => [...prev, ...prep]);
    }
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [tarefa]);

  const aprovarM = useMutation({
    mutationFn: ({ aprovado, comentario }: { aprovado: boolean; comentario?: string }) =>
      aprovarConclusao(tarefaId, aprovado, comentario),
    onSuccess: () => invalidate(),
  });

  const criarSubM = useMutation({
    mutationFn: (texto: string) => criarSubitem(tarefaId, texto),
    onSuccess: () => {
      setNovoSubitem("");
      invalidate();
    },
  });
  const atualizarSubM = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: Partial<Subitem> }) =>
      atualizarSubitem(tarefaId, id, dados),
    onSuccess: () => invalidate(),
  });
  const deletarSubM = useMutation({
    mutationFn: (id: string) => deletarSubitem(tarefaId, id),
    onSuccess: () => invalidate(),
  });

  const toggleVisM = useMutation({
    mutationFn: (visivelParaCliente: boolean) => atualizarTarefa(tarefaId, { visivelParaCliente } as any),
    onSuccess: () => invalidate(),
  });

  // Edição inline (PDF Fase 02 — Cadastro #8 / Viz #6): prazo, responsável e
  // descrição editáveis clicando direto no campo, com autosave.
  const editM = useMutation({
    mutationFn: (dados: Record<string, any>) => atualizarTarefa(tarefaId, dados),
    onSuccess: () => invalidate(),
  });
  const podeEditarCampos = user?.perfil !== "cliente" && !(tarefa as any)?.arquivada;

  // MELHORIA 17 — mover de coluna pelo drawer. Reusa o PATCH /:id/status, então o
  // gate de aprovador (concluído → aprovação p/ não-gestor) e as automações de
  // play valem igual ao arrastar. invalidate atualiza o card e o quadro.
  const moverM = useMutation({
    mutationFn: (status: string) => moverTarefa(tarefaId, status as any),
    onSuccess: () => invalidate(),
    onError: (e: any) => alert(e?.response?.data?.error?.message || "Não foi possível mover o card."),
  });

  /**
   * GATE DE AJUSTE no seletor "Coluna:" — mesmo comportamento do ARRASTAR no
   * quadro. Bug reportado (ago/2026): mover pelo seletor mandava o card direto
   * pra "Ajustes" SEM abrir o "Registrar ajuste", então o ajuste ficava sem
   * categoria/motivo (e some do relatório de ajustes). Agora os dois caminhos
   * pedem o registro antes de mover.
   */
  const [ajustePendente, setAjustePendente] = useState<string | null>(null);
  const [salvandoAjuste, setSalvandoAjuste] = useState(false);

  function pedirMover(novoStatus: string) {
    if (novoStatus === tarefa?.status) return;
    if (novoStatus === "ajustes") {
      setAjustePendente(novoStatus);
      return;
    }
    moverM.mutate(novoStatus);
  }

  async function confirmarAjuste(d: DadosAjuste) {
    if (!ajustePendente) return;
    setSalvandoAjuste(true);
    try {
      const { categoria, motivoId, descricao, arquivos } = d as any;
      // Move primeiro (o backend valida o gate/automações), depois registra o
      // ajuste — mesma ordem do quadro, pra não registrar ajuste de um movimento
      // que o backend recusaria.
      await moverM.mutateAsync(ajustePendente);
      const { falhas } = await registrarAjusteComAnexos(
        tarefaId,
        { categoria, motivoId: motivoId || null, descricao: descricao || null },
        arquivos || [],
      );
      if (falhas?.length) alert(`Ajuste registrado, mas ${falhas.length} anexo(s) falharam.`);
      setAjustePendente(null);
      invalidate();
    } catch (e: any) {
      alert(e?.response?.data?.error?.message || "Não foi possível registrar o ajuste.");
    } finally {
      setSalvandoAjuste(false);
    }
  }

  // Edição de comentário (Comentários #2) — só o autor, só tipo comentario.
  const editFbM = useMutation({
    mutationFn: ({ feedbackId, texto }: { feedbackId: string; texto: string }) => editarFeedback(tarefaId, feedbackId, texto),
    onSuccess: () => invalidate(),
    onError: (e: any) => alert(e?.response?.data?.error?.message || "Não foi possível editar o comentário."),
  });

  // "Trabalhando agora" — toggle entre marcar/desmarcar esta tarefa como foco.
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe });
  const ehFocoAtual = me?.tarefaAtualId === tarefaId;
  // Outra pessoa (que não eu) que está com a tarefa "Estou nessa" marcada.
  const ocupanteOutro = (tarefa as any)?.trabalhandoAgora?.find((u: any) => u.id !== me?.id);
  const setarFocoM = useMutation({
    mutationFn: (tid: string | null) => definirTarefaAtual(tid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["trabalhando-agora"] });
      qc.invalidateQueries({ queryKey: ["tarefa", tarefaId] });
    },
    onError: (e: any) => {
      const erro = e?.response?.data?.error;
      if (erro?.code === "TAREFA_OCUPADA") {
        alert(`Não pode pegar: ${erro.message}`);
      } else {
        alert(erro?.message || "Falha ao atualizar foco");
      }
    },
  });

  const arquivarM = useMutation({
    mutationFn: (motivo?: string) => arquivarTarefa(tarefaId, motivo),
    onSuccess: () => invalidate(),
  });
  const desarquivarM = useMutation({
    mutationFn: () => desarquivarTarefa(tarefaId),
    onSuccess: () => invalidate(),
  });
  const aprovarArqM = useMutation({
    mutationFn: () => aprovarArquivamentoTarefa(tarefaId),
    onSuccess: () => invalidate(),
  });
  const rejeitarArqM = useMutation({
    mutationFn: (motivo?: string) => rejeitarArquivamentoTarefa(tarefaId, motivo),
    onSuccess: () => invalidate(),
  });

  const deletarM = useMutation({
    mutationFn: () => deletarTarefa(tarefaId),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const duplicarM = useMutation({
    mutationFn: () => duplicarTarefa(tarefaId),
    onSuccess: (nova) => {
      invalidate();
      // Abre a cópia pra editar na hora; se o parent não tratar, só fecha.
      if (onDuplicada && nova?.id) onDuplicada(nova.id);
      else onClose();
    },
  });

  function handleExcluir() {
    if (!tarefa) return;
    const ok = confirmarDuasVezes({
      primeiraMensagem: `Excluir definitivamente a tarefa "${tarefa.titulo}"?\n\nFeedbacks, anexos, subitens e comentários serão apagados.`,
      segundaMensagem: `CONFIRMAR exclusão de "${tarefa.titulo}"?\n\nEsta é a confirmação FINAL — não dá pra desfazer.`,
    });
    if (ok) deletarM.mutate();
  }

  const ehAdmin = user?.perfil === "admin";
  const [showEditar, setShowEditar] = useState(false);

  // Aprovar conclusão: o aprovador nomeado OU qualquer gestor/admin (assim não
  // fica preso no aprovador nomeado — ex.: head de produção viajando).
  const podeAprovar = !!tarefa && tarefa.status === "aguardando_aprovacao" && (tarefa.aprovadorId === user?.id || user?.perfil === "gestor" || user?.perfil === "admin");
  const podeEditarVis = user?.perfil === "gestor" || user?.perfil === "admin";
  const ehGestor = user?.perfil === "gestor" || user?.perfil === "admin";
  const ehCriador = !!tarefa && tarefa.criadoPor === user?.id;
  const podeArquivarDireto = ehGestor || ehCriador;
  const podeEditar = ehGestor || ehCriador;
  const temSolicitacaoArquivamento = !!tarefa?.solicArqPor;
  const urg = tarefa ? urgenciaTarefa(tarefa) : null;
  const adiamentos = tarefa?.feedbacks?.filter((f) => f.tipo === "adiamento").length || 0;

  return (
    // Clicar no fundo escuro NÃO fecha (pedido da Rec, jul/2026): um clique fora
    // sem querer apagava o comentário/edição em andamento. Só o X fecha.
    // Rede de segurança do ARRASTAR IMAGEM: se a pessoa soltar o arquivo FORA da
    // caixa de texto, o navegador abriria a imagem e ela perderia o card aberto.
    // Aqui só cancelamos o comportamento padrão (não fazemos nada com o arquivo).
    // Drop DENTRO do editor não chega aqui — o MentionTextarea dá stopPropagation.
    <div
      className="fixed inset-0 z-40 flex justify-end"
      onDragOver={(e) => { if (Array.from(e.dataTransfer?.types || []).includes("Files")) e.preventDefault(); }}
      onDrop={(e) => { if (Array.from(e.dataTransfer?.types || []).includes("Files")) e.preventDefault(); }}
    >
      <div className="absolute inset-0 bg-black/40" />
      {/* Drawer em DUAS COLUNAS a partir de lg (pedido do Ricardo, jul/2026 —
          referência: card do Trello). Antes era uma coluna só de max-w-xl e os
          comentários ficavam no FIM: a pessoa rolava prazo, tempo, descrição,
          checklist e anexos antes de chegar neles. Agora o painel é mais largo
          e vira flex-col: o corpo rola por DENTRO, cada coluna com sua barra.
          Abaixo de lg continua empilhado (detalhes e depois comentários), que
          é o comportamento antigo — em tela estreita não cabe lado a lado. */}
      <div className="relative w-full sm:max-w-xl lg:max-w-5xl bg-white h-full shadow-2xl flex flex-col">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between z-10 gap-2">
          <h3 className="font-semibold text-slate-800 truncate flex-1">{tarefa?.titulo || "Carregando…"}</h3>
          {tarefa && (
            <button
              onClick={() => setShowAtividade((v) => !v)}
              className={
                "flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors shrink-0 " +
                (showAtividade
                  ? "bg-blue-600 text-white"
                  : "text-slate-500 hover:text-blue-600 hover:bg-blue-50 border border-slate-200")
              }
              title="Ver tudo que aconteceu neste card"
            >
              <History size={14} />
              <span className="hidden sm:inline">Atividade</span>
            </button>
          )}
          {podeEditar && tarefa && (
            <button
              onClick={() => setShowEditar(true)}
              className="p-1.5 rounded text-slate-400 hover:text-blue-600 hover:bg-blue-50"
              title="Editar tarefa"
            >
              <Pencil size={16} />
            </button>
          )}
          {tarefa && (
            <button
              onClick={() => duplicarM.mutate()}
              disabled={duplicarM.isPending}
              className="p-1.5 rounded text-slate-400 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-50"
              title="Duplicar tarefa (cria uma cópia pra editar)"
            >
              <Copy size={16} />
            </button>
          )}
          {ehAdmin && tarefa && (
            <button
              onClick={handleExcluir}
              disabled={deletarM.isPending}
              className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50"
              title="Excluir tarefa definitivamente"
            >
              <Trash2 size={16} />
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white shadow-sm"
            title="Fechar a tarefa"
          >
            <X size={18} />
          </button>
        </div>

        {showEditar && tarefa && (
          <NovaTarefaModal
            editing={tarefa}
            onClose={() => setShowEditar(false)}
          />
        )}

        {showAtividade && tarefa ? (
          <AtividadeTarefa tarefaId={tarefa.id} onVoltar={() => setShowAtividade(false)} />
        ) : isLoading || !tarefa ? (
          <div className="p-5 text-sm text-slate-500">Carregando…</div>
        ) : (
          // Corpo: rola por fora até lg (uma coluna); em lg+ o container segura
          // a altura e cada coluna ganha a própria rolagem.
          <div className="flex-1 min-h-0 overflow-y-auto lg:overflow-hidden lg:flex lg:items-stretch">
          {/* COLUNA 1 — detalhes do card */}
          <div className="p-5 space-y-5 lg:flex-1 lg:min-w-0 lg:overflow-y-auto">
            {/* Cabeçalho info */}
            <div>
              {urg && (
                <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase ${urg.badge}`}>
                  {urg.texto}
                </span>
              )}
              <div className="text-xs text-slate-500 mt-2 flex items-center gap-1.5 flex-wrap">
                {tarefa.projeto ? (
                  <>
                    <span>Projeto:</span>
                    <ProjetoAvatar
                      nome={tarefa.projeto.nome}
                      fotoUrl={tarefa.projeto.fotoUrl}
                      tamanho="xs"
                      formato="rounded"
                    />
                    <strong className="text-slate-700">{tarefa.projeto.nome}</strong>
                    {tarefa.projeto.clienteRef?.nome && (
                      <span>· Cliente: <strong className="text-slate-700">{tarefa.projeto.clienteRef.nome}</strong></span>
                    )}
                  </>
                ) : (
                  <span>Tarefa avulsa</span>
                )}
                {/* MELHORIA 17 — mover de coluna com o card aberto (estilo Trello). */}
                {podeEditarCampos ? (
                  <span className="inline-flex items-center gap-1">
                    · Coluna:{" "}
                    <select
                      value={tarefa.status}
                      onChange={(e) => pedirMover(e.target.value)}
                      disabled={moverM.isPending}
                      className="text-xs rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-1.5 py-0.5 outline-none focus:border-blue-500 cursor-pointer font-medium disabled:opacity-60"
                      title="Mover o card para outra coluna"
                    >
                      {!colunas.some((c) => c.chave === tarefa.status) && (
                        <option value={tarefa.status}>{STATUS_LABELS[tarefa.status] || tarefa.status}</option>
                      )}
                      {colunas.map((c) => (
                        <option key={c.id} value={c.chave}>{c.emoji} {c.label}</option>
                      ))}
                    </select>
                  </span>
                ) : (
                  <span>· Status: {STATUS_LABELS[tarefa.status] || tarefa.status}</span>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Atribuída a{" "}
                <EditavelAtribuido
                  atual={tarefa.atribuido}
                  usuarios={usuarios}
                  podeEditar={podeEditarCampos}
                  onSalvar={(uid) => editM.mutate({ atribuidoA: uid })}
                />
                {tarefa.criador && <> · criada por <strong>{tarefa.criador.nome}</strong></>}
                {tarefa.aprovador && <> · aprovador <strong>{tarefa.aprovador.nome}</strong></>}
                {tarefa.recorrencia && <> · recorrência: {tarefa.recorrencia}</>}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                Prazo:{" "}
                <EditavelPrazo
                  prazoISO={tarefa.prazo}
                  podeEditar={podeEditarCampos}
                  onSalvar={(iso) => editM.mutate({ prazo: iso })}
                />
                {tarefa.prazoOriginal && tarefa.prazo !== tarefa.prazoOriginal && (
                  <> (original: {new Date(tarefa.prazoOriginal).toLocaleDateString("pt-BR")} — adiada {adiamentos}x)</>
                )}
              </p>
              {/* Data combinada com o cliente — SÓ gestão vê e edita (Fase 02, item 16).
                  MELHORIA 16 — campo de data DIRETO (antes era um "—" clicável, pouco
                  intuitivo). Vazio mostra dd/mm/aaaa; limpar salva null. */}
              {ehGestor && (
                <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                  <span className="text-xs text-purple-600 dark:text-purple-400 font-medium whitespace-nowrap">🔒 Entrega ao cliente:</span>
                  <input
                    type="date"
                    value={(tarefa as any).dataEntregaCliente ? String((tarefa as any).dataEntregaCliente).slice(0, 10) : ""}
                    disabled={!podeEditarCampos}
                    onChange={(e) => editM.mutate({ dataEntregaCliente: e.target.value ? `${e.target.value}T12:00:00` : null })}
                    className="px-2 py-1 rounded-lg border border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 bg-white dark:bg-slate-800 outline-none focus:border-purple-500 text-xs disabled:opacity-60"
                    title="Data combinada com o cliente, separada do prazo interno. Alimenta a pauta. Só gestão vê."
                  />
                  <span className="text-[10px] text-slate-400">(só gestão vê · alimenta a pauta)</span>
                </div>
              )}
              {(tarefa as any).tipoTarefa && (
                <p className="text-xs text-slate-500 mt-0.5">
                  Tipo:{" "}
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium"
                    style={{ background: (tarefa as any).tipoTarefa.cor || "#64748b", color: "#fff" }}
                  >
                    {(tarefa as any).tipoTarefa.icone} {(tarefa as any).tipoTarefa.label}
                  </span>
                </p>
              )}
              {/* Card ABERTO mostra o nome de cada pessoa e a FUNÇÃO dela nesta
                  tarefa — no card fechado só aparecem os avatares (pedido da
                  Thainá, ago/2026). A função da principal vive em
                  `funcaoPrincipal`; a dos demais, em `responsaveis[].funcao`. */}
              {(tarefa as any).funcaoPrincipal && (
                <p className="text-xs text-slate-500 mt-0.5">
                  Função: <strong className="text-slate-700">{(tarefa as any).funcaoPrincipal}</strong>
                </p>
              )}
              {Array.isArray((tarefa as any).responsaveis) && (tarefa as any).responsaveis.length > 0 && (
                <p className="text-xs text-slate-500 mt-0.5">
                  Também responsáveis:{" "}
                  <strong className="text-slate-700">
                    {(tarefa as any).responsaveis
                      .filter((r: any) => r.usuario?.nome)
                      .map((r: any) => r.usuario.nome + (r.funcao ? ` (${r.funcao})` : ""))
                      .join(", ")}
                  </strong>
                </p>
              )}
              {(tarefa as any).linkDrive && (
                <p className="text-xs text-slate-500 mt-0.5"><LinkExterno url={(tarefa as any).linkDrive}>📁 Pasta no Drive</LinkExterno></p>
              )}
              {tarefa.projeto?.clienteRef?.linkAssets && (
                <p className="text-xs text-slate-500 mt-0.5"><LinkExterno url={tarefa.projeto.clienteRef.linkAssets}>🗂️ Assets do cliente</LinkExterno></p>
              )}
              <ProgramadoExecutado horasEstimadas={tarefa.horasEstimadas} tempoExecutadoMs={(tarefa as any).tempoExecutadoMs} />
              <TempoTrabalhado
                tarefaId={tarefa.id}
                tempoPorPessoa={((tarefa as any).tempoPorPessoa || []) as TempoPessoa[]}
                meId={me?.id}
                ehGestor={ehGestor}
              />
              <EntregasCard tarefaId={tarefa.id} meId={me?.id} ehGestor={ehGestor} />
              {/* BRIEFING editável — aparece se houver formulário pro tipo da tarefa. */}
              <BriefingSecao
                escopo="tarefa"
                alvoId={tarefa.id}
                tipoTarefaId={(tarefa as any).tipoTarefaId}
                podeEditar={podeEditarCampos}
              />
              <SecaoAjustes tarefaId={tarefa.id} />
              <EditavelDescricao
                valor={tarefa.descricao}
                podeEditar={podeEditarCampos}
                usuarios={usuarios}
                onSalvar={(v) => editM.mutateAsync({ descricao: v })}
                onUploadImagem={(file) => uploadAnexo(tarefaId, file).then((a) => anexoUrlAbsoluto(a.url))}
              />
              {podeEditarVis && (
                <label className="flex items-center gap-2 text-xs text-slate-600 mt-2">
                  <input
                    type="checkbox"
                    checked={!!(tarefa as any).visivelParaCliente}
                    onChange={(e) => toggleVisM.mutate(e.target.checked)}
                  />
                  Visível ao cliente no portal
                </label>
              )}

              {/* "Trabalhando agora" — 1 pessoa por tarefa. Mostra ocupante se houver. */}
              {user?.perfil !== "cliente" && tarefa.status !== "concluido" && !(tarefa as any).arquivada && (
                <div className="mt-3">
                  {ocupanteOutro ? (
                    <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 px-2.5 py-1.5 rounded-lg text-xs">
                      <Avatar nome={ocupanteOutro.nome} fotoUrl={ocupanteOutro.fotoUrl} tamanho={20} bgFallback="bg-amber-200" textFallback="text-amber-800" />
                      <div className="leading-tight">
                        <div className="font-semibold">{ocupanteOutro.nome} está nessa</div>
                        <div className="text-[10px] opacity-80">há {tempoDesde(ocupanteOutro.tarefaAtualDesde)}</div>
                      </div>
                    </div>
                  ) : (
                    (() => {
                      // Se já estou focando OUTRA tarefa, deixo explícito que clicar troca.
                      const focandoOutra = !!me?.tarefaAtual && me.tarefaAtual.id !== tarefaId;
                      return (
                        <button
                          type="button"
                          onClick={() => setarFocoM.mutate(ehFocoAtual ? null : tarefaId)}
                          disabled={setarFocoM.isPending}
                          className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors disabled:opacity-50 ${
                            ehFocoAtual
                              ? "bg-emerald-100 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                              : focandoOutra
                                ? "bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100"
                                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                          }`}
                          title={
                            ehFocoAtual
                              ? "Você está marcado como trabalhando nesta tarefa. Clique para sair."
                              : focandoOutra
                                ? `Vai trocar — sai automaticamente de "${me!.tarefaAtual!.titulo}".`
                                : "Marque para indicar que está focando nesta tarefa agora."
                          }
                        >
                          <Focus size={13} />
                          {ehFocoAtual
                            ? "Estou nessa — Sair"
                            : focandoOutra
                              ? <>Trocar pra esta <span className="opacity-60">(sai de “{me!.tarefaAtual!.titulo}”)</span></>
                              : "Estou nessa"}
                        </button>
                      );
                    })()
                  )}
                </div>
              )}
            </div>

            {/* Aprovação */}
            {podeAprovar && (
              <div className="border border-amber-300 bg-amber-50 rounded-lg p-3">
                <p className="text-sm font-medium text-amber-800 mb-2">
                  {tarefa.aprovadorId === user?.id
                    ? "Esta tarefa aguarda sua aprovação como revisor."
                    : "Esta tarefa aguarda aprovação interna — você pode aprovar como gestor/admin."}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => aprovarM.mutate({ aprovado: true, comentario: texto })}
                    className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded text-sm"
                  >
                    <ThumbsUp size={14} /> Aprovar
                  </button>
                  <button
                    onClick={() => aprovarM.mutate({ aprovado: false, comentario: texto })}
                    className="flex items-center gap-1 bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded text-sm"
                  >
                    <ThumbsDown size={14} /> Rejeitar
                  </button>
                </div>
              </div>
            )}

            {/* Tarefa arquivada — banner */}
            {tarefa.arquivada && (
              <div className="border border-slate-300 bg-slate-100 rounded-lg p-3 flex items-center justify-between gap-2">
                <p className="text-sm text-slate-700 flex items-center gap-2">
                  <Archive size={14} /> Esta tarefa está arquivada
                  {tarefa.arquivadaEm && <span className="text-xs text-slate-500">desde {new Date(tarefa.arquivadaEm).toLocaleDateString("pt-BR")}</span>}
                </p>
                {ehGestor && (
                  <button
                    onClick={() => desarquivarM.mutate()}
                    className="text-xs flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1 rounded"
                  >
                    <ArchiveRestore size={12} /> Desarquivar
                  </button>
                )}
              </div>
            )}

            {/* Solicitação de arquivamento pendente — visível pra gestor/admin/criador */}
            {temSolicitacaoArquivamento && !tarefa.arquivada && (ehGestor || ehCriador) && (
              <div className="border border-amber-300 bg-amber-50 rounded-lg p-3">
                <p className="text-sm font-medium text-amber-800 mb-1">Solicitação de arquivamento pendente</p>
                <p className="text-xs text-amber-700 mb-2">
                  {tarefa.solicArqMotivo ? `"${tarefa.solicArqMotivo}"` : "Sem motivo informado."}
                  {tarefa.solicArqEm && <span className="text-amber-600 ml-1">· {new Date(tarefa.solicArqEm).toLocaleString("pt-BR")}</span>}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => aprovarArqM.mutate()}
                    className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded text-xs"
                  >
                    <ThumbsUp size={12} /> Aprovar arquivamento
                  </button>
                  <button
                    onClick={() => {
                      const motivo = window.prompt("Motivo da rejeição (opcional):") || undefined;
                      rejeitarArqM.mutate(motivo);
                    }}
                    className="flex items-center gap-1 bg-slate-200 hover:bg-slate-300 text-slate-700 px-3 py-1.5 rounded text-xs"
                  >
                    <ThumbsDown size={12} /> Rejeitar
                  </button>
                </div>
              </div>
            )}

            {/* Botão Arquivar / Solicitar — não mostra se já arquivada nem se já solicitou */}
            {!tarefa.arquivada && !temSolicitacaoArquivamento && (
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    if (podeArquivarDireto) {
                      arquivarM.mutate(undefined);
                    } else {
                      const motivo = window.prompt("Motivo da solicitação de arquivamento (opcional):") || undefined;
                      arquivarM.mutate(motivo);
                    }
                  }}
                  className="flex items-center gap-1 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded"
                  title={podeArquivarDireto ? "Arquivar tarefa (inativa)" : "Pedir arquivamento — gestor/admin precisa aprovar"}
                >
                  <Archive size={12} /> {podeArquivarDireto ? "Arquivar" : "Solicitar arquivamento"}
                </button>
              </div>
            )}

            {/* Confirmação de solicitação enviada (pra quem solicitou e não pode arquivar direto) */}
            {temSolicitacaoArquivamento && !tarefa.arquivada && !ehGestor && !ehCriador && (
              <div className="border border-slate-200 bg-slate-50 rounded-lg p-3 text-xs text-slate-600">
                ⏳ Você solicitou arquivamento desta tarefa. Aguardando aprovação do gestor/criador.
              </div>
            )}

            {/* Subitens / checklist — com drag-and-drop e edição inline (igual notas pessoais) */}
            <div>
              <h4 className="text-xs font-semibold uppercase text-slate-500 mb-2">Checklist</h4>
              <DragDropContext
                onDragEnd={(result: DropResult) => {
                  if (!result.destination) return;
                  if (result.destination.index === result.source.index) return;
                  const itens = tarefa.subitens || [];
                  const arrastado = itens[result.source.index];
                  const sem = itens.filter((s) => s.id !== arrastado.id);
                  const acima = sem[result.destination.index - 1];
                  const abaixo = sem[result.destination.index];
                  let novaOrdem: number;
                  if (!acima && !abaixo) novaOrdem = 0;
                  else if (!acima) novaOrdem = (abaixo!.ordem ?? 0) - 1000;
                  else if (!abaixo) novaOrdem = (acima.ordem ?? 0) + 1000;
                  else novaOrdem = ((acima.ordem ?? 0) + (abaixo.ordem ?? 0)) / 2;
                  atualizarSubM.mutate({ id: arrastado.id, dados: { ordem: novaOrdem } });
                }}
              >
                <Droppable droppableId="subitens">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-1">
                      {(tarefa.subitens || []).map((s, index) => (
                        <Draggable key={s.id} draggableId={s.id} index={index}>
                          {(prov, snap) => (
                            <div
                              ref={prov.innerRef}
                              {...prov.draggableProps}
                              className={snap.isDragging ? "opacity-90" : ""}
                            >
                              <SubitemRow
                                s={s}
                                dragHandleProps={prov.dragHandleProps}
                                onToggleConcluido={() => atualizarSubM.mutate({ id: s.id, dados: { concluido: !s.concluido } })}
                                onEditar={(texto) => atualizarSubM.mutate({ id: s.id, dados: { texto } })}
                                onExcluir={() => deletarSubM.mutate(s.id)}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
              <div className="flex items-center gap-2 mt-2">
                <Plus size={14} className="text-slate-400" />
                <input
                  type="text"
                  value={novoSubitem}
                  onChange={(e) => setNovoSubitem(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && novoSubitem.trim()) {
                      e.preventDefault();
                      criarSubM.mutate(novoSubitem.trim());
                    }
                  }}
                  placeholder="Adicionar item…"
                  className="flex-1 text-sm border-b border-slate-200 focus:border-blue-400 outline-none py-1"
                />
              </div>
            </div>

            {/* Anexos da tarefa (não vinculados a feedback — os de comentário
                aparecem inline na timeline, estilo WhatsApp). */}
            {(() => {
              // Não repete na lista de Anexos as imagens que já aparecem inline
              // (coladas na descrição ou num comentário) — senão apareciam 2x.
              const inlineUrls = new Set<string>();
              const coletarInline = (t?: string | null) => { for (const m of (t || "").matchAll(/!\[[^\]]*\]\(([^)]+)\)/g)) inlineUrls.add(m[1]); };
              coletarInline(tarefa.descricao);
              (tarefa.feedbacks || []).forEach((fb) => coletarInline(fb.texto));
              const avulsos = (tarefa.anexos || []).filter((a) => !a.feedbackId && !inlineUrls.has(a.url) && !inlineUrls.has(anexoUrlAbsoluto(a.url)));
              if (avulsos.length === 0) return null;
              return (
                <div>
                  <h4 className="text-xs font-semibold uppercase text-slate-500 mb-2">Anexos</h4>
                  <div className="space-y-2">
                    {avulsos.map((a) => (
                      <AnexoView key={a.id} anexo={a} tarefaId={tarefa.id} />
                    ))}
                  </div>
                </div>
              );
            })()}

          </div>

          {/* COLUNA 2 — conversa (compositor + histórico). Fundo levemente
              diferente pra separar do card sem precisar de borda grossa.
              Largura fixa: a conversa não deve espremer os detalhes. */}
          {/* `bg-slate-50` SEM opacidade de propósito. O dark mode deste app é
              sobrescrita global de classe (`html.dark .bg-slate-50 {…}` no
              index.css); com o modificador `/60` o nome da classe vira
              `bg-slate-50\/60`, a regra do dark NÃO casa e no escuro sobrava um
              cinza claro lavado por cima do fundo escuro (bug reportado pelo
              Ricardo em 21/jul/2026). Vale pra qualquer cor daqui: neste projeto,
              cor com barra de opacidade não acompanha o tema. */}
          <div className="p-5 space-y-4 border-t border-slate-200 lg:border-t-0 lg:border-l lg:w-[400px] lg:shrink-0 lg:overflow-y-auto bg-slate-50">
            {/* Composer de feedback */}
            <div className="border border-slate-200 bg-white rounded-lg p-3 space-y-2">
              <h4 className="text-xs font-semibold uppercase text-slate-500">Novo feedback</h4>
              <MentionTextarea
                value={texto}
                onChange={setTexto}
                usuarios={usuarios}
                placeholder="Escreva um comentário (use @nome; cole com Ctrl+V ou ARRASTE a imagem pra cá — ela entra no meio do texto)…"
                rows={3}
                toolbar
                onUploadImagem={(file) => uploadAnexo(tarefaId, file).then((a) => anexoUrlAbsoluto(a.url))}
              />
              <p className="text-[10px] text-slate-400 -mt-1">
                Cole (Ctrl+V) ou <strong>arraste</strong> a imagem pra inserir no meio do texto · botões acima pra negrito/itálico/grifar/cor · "Anexar" pra anexos avulsos.
              </p>

              {showAdiar && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-slate-600">Novo prazo:</span>
                  <input
                    type="date"
                    value={novoPrazo}
                    onChange={(e) => setNovoPrazo(e.target.value)}
                    className="border border-slate-300 rounded px-2 py-1 text-sm"
                  />
                </div>
              )}

              {/* Preview dos anexos preparados (colados/escolhidos) — vão junto com o comentário. */}
              {anexosPendentes.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {anexosPendentes.map((f, i) => (
                    <div key={i} className="relative">
                      {f.type.startsWith("image/") ? (
                        <img src={URL.createObjectURL(f)} alt={f.name} className="w-16 h-16 object-cover rounded border border-slate-200" />
                      ) : (
                        <div className="w-16 h-16 rounded border border-slate-200 bg-slate-50 flex items-center justify-center" title={f.name}>
                          <Paperclip size={16} className="text-slate-400" />
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => setAnexosPendentes((p) => p.filter((_, idx) => idx !== i))}
                        className="absolute -top-1.5 -right-1.5 bg-slate-700 text-white rounded-full p-0.5 opacity-90 hover:bg-red-600"
                        title="Remover"
                      >
                        <X size={11} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => feedbackM.mutate({ tipo: "comentario", texto })}
                  disabled={(!texto.trim() && anexosPendentes.length === 0) || feedbackM.isPending}
                  title="Só adiciona um comentário/feedback. NÃO muda o status da tarefa."
                  className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white text-sm px-3 py-1.5 rounded disabled:opacity-50"
                >
                  <Send size={14} /> {anexosPendentes.length > 0 && !texto.trim() ? "Enviar" : "Comentar"}
                </button>

                {!showAdiar ? (
                  <button
                    onClick={() => setShowAdiar(true)}
                    className="flex items-center gap-1 bg-amber-100 hover:bg-amber-200 text-amber-800 text-sm px-3 py-1.5 rounded"
                  >
                    <Clock size={14} /> Adiar
                  </button>
                ) : (
                  <button
                    onClick={() => feedbackM.mutate({ tipo: "adiamento", texto, novoPrazo })}
                    disabled={!novoPrazo || feedbackM.isPending}
                    className="flex items-center gap-1 bg-amber-500 hover:bg-amber-600 text-white text-sm px-3 py-1.5 rounded disabled:opacity-50"
                  >
                    <Clock size={14} /> Confirmar adiamento
                  </button>
                )}

                {tarefa.status !== "concluido" && tarefa.status !== "aguardando_aprovacao" && (
                  <button
                    onClick={() => feedbackM.mutate({ tipo: "conclusao", texto })}
                    title="Marca a tarefa como CONCLUÍDA (se houver aprovador, vai para aprovação). Para só deixar um recado, use 'Comentar'."
                    className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-sm px-3 py-1.5 rounded"
                  >
                    <CheckCircle2 size={14} /> Concluir
                  </button>
                )}
                {tarefa.status === "concluido" && (
                  <button
                    onClick={() => feedbackM.mutate({ tipo: "reabertura", texto })}
                    className="flex items-center gap-1 bg-slate-200 hover:bg-slate-300 text-slate-700 text-sm px-3 py-1.5 rounded"
                  >
                    <RotateCcw size={14} /> Reabrir
                  </button>
                )}

                <div className="ml-auto flex items-center gap-2">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-1 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm px-3 py-1.5 rounded"
                    title="Anexar arquivo"
                  >
                    <Paperclip size={14} /> Anexar
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      const fs = Array.from(e.target.files || []);
                      if (fs.length) setAnexosPendentes((p) => [...p, ...fs]);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                  />
                  <AudioRecorder onReady={(blob, nome) => uploadM.mutate(new File([blob], nome, { type: blob.type }))} />
                </div>
              </div>
              {uploadM.isPending && <p className="text-xs text-slate-500">Enviando anexo…</p>}
            </div>

            {/* Timeline */}
            <div>
              <h4 className="text-xs font-semibold uppercase text-slate-500 mb-2">Histórico</h4>
              <div className="space-y-3">
                {tarefa.feedbacks?.length === 0 && (
                  <p className="text-sm text-slate-400">Nenhum feedback ainda.</p>
                )}
                {tarefa.feedbacks?.map((f) => (
                  <FeedbackItem
                    key={f.id}
                    f={f}
                    tarefaId={tarefa.id}
                    meId={me?.id}
                    salvando={editFbM.isPending}
                    onSalvar={(texto) => editFbM.mutate({ feedbackId: f.id, texto })}
                    usuarios={usuarios}
                  />
                ))}
              </div>
            </div>
          </div>
          </div>
        )}
      </div>

      {/* Gate de ajuste ao mover pelo seletor "Coluna:" — igual ao arrastar. */}
      {ajustePendente && (
        <RegistrarAjusteModal
          onConfirmar={confirmarAjuste}
          onCancelar={() => setAjustePendente(null)}
          salvando={salvandoAjuste}
          tarefaId={tarefaId}
        />
      )}
    </div>
  );
}

// Descreve um evento de auditoria em linguagem natural (pt-BR).
function descreverAtividade(e: EventoHistorico): string {
  const d = (e.detalhes || {}) as any;
  switch (e.acao) {
    case "criar":
      return d?.duplicadaDe ? "criou o card (duplicando outro)" : "criou o card";
    case "mover_status":
      return d?.paraLabel
        ? `moveu de "${d.deLabel ?? "—"}" para "${d.paraLabel}"`
        : "moveu o card de coluna";
    case "editar":
      return Array.isArray(d?.campos) && d.campos.length
        ? `alterou ${d.campos.join(", ")}`
        : "editou o card";
    case "atualizar":
      if (d?.ajuste) return "registrou um ajuste";
      if (d?.ajusteRemovido) return "removeu um ajuste";
      if (d?.tempoAjuste) return "corrigiu o tempo trabalhado";
      if (d?.tempoAjusteAprovado) return "aprovou uma correção de tempo";
      if (d?.tempoAjusteRejeitado) return "rejeitou uma correção de tempo";
      if (d?.comentario) {
        const t = d.comentario.tipo;
        if (t === "adiamento") return "adiou o prazo";
        if (t === "conclusao") return "marcou como concluída";
        if (t === "reabertura") return "reabriu o card";
        return "comentou";
      }
      if (d?.comentarioEditado) return "editou um comentário";
      if (d?.anexo) return `anexou "${d.anexo.nome}"`;
      if (d?.anexoRemovido) return "removeu um anexo";
      if (d?.subitem) return `adicionou o subitem "${d.subitem.texto}"`;
      if (d?.subitemMarcado)
        return `${d.subitemMarcado.concluido ? "concluiu" : "desmarcou"} o subitem "${d.subitemMarcado.texto}"`;
      if (d?.subitemEditado) return `editou o subitem "${d.subitemEditado.texto}"`;
      if (d?.subitemRemovido)
        return d.subitemRemovido.texto ? `removeu o subitem "${d.subitemRemovido.texto}"` : "removeu um subitem";
      if (d?.aprovacao) return d.aprovacao.aprovado ? "aprovou a conclusão" : "reprovou a conclusão";
      return "editou o card";
    case "deletar": return "excluiu o card";
    case "arquivar": return "arquivou o card";
    case "desarquivar": return "desarquivou o card";
    case "solicitar_arquivamento": return "solicitou arquivamento";
    case "aprovar_arquivamento": return "aprovou o arquivamento";
    case "rejeitar_arquivamento": return "rejeitou o arquivamento";
    default: return e.acao.replace(/_/g, " ");
  }
}

/**
 * Aba "Atividade" do card (estilo Trello): trilha completa de auditoria — quem
 * criou, moveu de coluna, editou campos, comentou, anexou, ajustou, arquivou…
 * Abre pelo botão do topo do drawer e ocupa o corpo inteiro.
 */
function AtividadeTarefa({ tarefaId, onVoltar }: { tarefaId: string; onVoltar: () => void }) {
  const { data: eventos = [], isLoading, isError } = useQuery({
    queryKey: ["tarefa-historico", tarefaId],
    queryFn: () => listarHistoricoTarefa(tarefaId),
    // Sempre fresco ao abrir: a pessoa costuma abrir logo depois de mexer no card.
    refetchOnMount: "always",
  });

  return (
    // `flex-1 min-h-0 overflow-y-auto`: o painel do drawer virou flex-col e NÃO
    // rola mais por fora (jul/2026). Sem isso, uma trilha de atividade longa
    // vazaria pra fora da tela sem barra de rolagem.
    <div className="p-5 flex-1 min-h-0 overflow-y-auto">
      <button
        type="button"
        onClick={onVoltar}
        className="flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-blue-600 mb-3"
      >
        <ChevronDown size={14} className="rotate-90" /> Voltar para o card
      </button>

      <h4 className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1">
        <History size={15} /> Atividade do card
      </h4>
      <p className="text-[11px] text-slate-400 mb-4">
        Tudo que aconteceu neste card, do mais recente para o mais antigo.
      </p>

      {isLoading && <p className="text-sm text-slate-400">Carregando…</p>}
      {isError && <p className="text-sm text-red-500">Não foi possível carregar a atividade.</p>}
      {!isLoading && !isError && eventos.length === 0 && (
        <p className="text-sm text-slate-400">Nenhum registro de atividade para este card ainda.</p>
      )}

      <ol className="space-y-2.5">
        {eventos.map((e) => (
          <li key={e.id} className="flex gap-2.5 text-sm">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-slate-300 dark:bg-slate-600 shrink-0" aria-hidden />
            <div className="min-w-0">
              <p className="text-slate-700 dark:text-slate-200">
                <strong>{e.autorNome || "Sistema"}</strong>{" "}
                <span className="text-slate-600 dark:text-slate-300">{descreverAtividade(e)}</span>
              </p>
              <p className="text-[11px] text-slate-400">
                {new Date(e.criadoEm).toLocaleString("pt-BR")}
              </p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function SubitemRow({ s, dragHandleProps, onToggleConcluido, onEditar, onExcluir }: {
  s: Subitem;
  dragHandleProps?: any;
  onToggleConcluido: () => void;
  onEditar: (texto: string) => void;
  onExcluir: () => void;
}) {
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState(s.texto);

  function commit() {
    const t = texto.trim();
    if (t && t !== s.texto) onEditar(t);
    else setTexto(s.texto);
    setEditando(false);
  }

  return (
    <div className="group flex items-center gap-2 px-1 py-0.5 rounded hover:bg-slate-50 dark:hover:bg-slate-800/50">
      <span
        {...dragHandleProps}
        className="shrink-0 text-slate-300 hover:text-slate-500 dark:text-slate-600 dark:hover:text-slate-400 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
        title="Arrastar para reordenar"
      >
        <GripVertical size={14} />
      </span>
      <button
        type="button"
        onClick={onToggleConcluido}
        className={s.concluido ? "text-emerald-600 shrink-0" : "text-slate-400 hover:text-slate-600 shrink-0"}
        title={s.concluido ? "Desmarcar" : "Marcar como feito"}
      >
        {s.concluido ? <CheckSquare size={16} /> : <Square size={16} />}
      </button>
      {editando ? (
        <input
          type="text"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            if (e.key === "Escape") { setTexto(s.texto); setEditando(false); }
          }}
          autoFocus
          className="flex-1 px-2 py-0.5 rounded border border-blue-300 dark:border-blue-600 dark:bg-slate-800 dark:text-slate-100 text-sm outline-none"
        />
      ) : (
        <span
          onClick={() => setEditando(true)}
          className={`flex-1 text-sm cursor-text [overflow-wrap:anywhere] ${
            s.concluido ? "line-through text-slate-400" : "text-slate-700 dark:text-slate-200"
          }`}
          title={s.texto}
        >
          {s.texto}
        </span>
      )}
      {s.autor?.nome && (
        <span
          className="text-[10px] text-slate-400 italic shrink-0"
          title={`Criado por ${s.autor.nome}`}
        >
          @{s.autor.nome.split(" ")[0].toLowerCase()}
        </span>
      )}
      <button
        type="button"
        onClick={onExcluir}
        className="p-1 rounded text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
        title="Excluir item"
      >
        <X size={14} />
      </button>
    </div>
  );
}

/** Um ajuste na lista: cabeçalho + anexos (foto/vídeo/pdf/áudio) + botão de adicionar mais arquivos. */
function AjusteItem({ ajuste, tarefaId, usuarios = [] }: { ajuste: TarefaAjuste; tarefaId: string; usuarios?: UsuarioAtivo[] }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const confirm = useConfirm();
  const inputRef = useRef<HTMLInputElement>(null);
  const [enviando, setEnviando] = useState(false);
  const [removendo, setRemovendo] = useState(false);
  const resolvido = !!ajuste.resolvidoEm;
  // Resolvido não pode excluir (vira registro travado); antes de resolver, quem
  // criou (ou gestor/admin) ainda pode remover engano.
  const podeRemover = !resolvido && (user?.id === ajuste.criadoPor || user?.perfil === "gestor" || user?.perfil === "admin");
  // Editar segue a MESMA régua do excluir: resolvido é registro fechado (pra
  // corrigir, reabre no ✓ e edita). O backend confere de novo.
  const podeEditar = podeRemover;

  // Edição do ajuste já postado (pedido do Ricardo, ago/2026).
  const [editando, setEditando] = useState(false);
  const [rascDesc, setRascDesc] = useState(ajuste.descricao || "");
  const [rascMotivo, setRascMotivo] = useState(ajuste.motivoId || "");
  const [rascCategoria, setRascCategoria] = useState<"interno" | "cliente">(
    ajuste.categoria === "cliente" ? "cliente" : "interno",
  );
  const { data: motivos = [] } = useQuery({
    queryKey: ["motivos-ajuste", "ativos"],
    queryFn: () => listarMotivosAjuste(),
    enabled: editando,
  });

  function abrirEdicao() {
    setRascDesc(ajuste.descricao || "");
    setRascMotivo(ajuste.motivoId || "");
    setRascCategoria(ajuste.categoria === "cliente" ? "cliente" : "interno");
    setEditando(true);
  }

  const resolverM = useMutation({
    mutationFn: (v: boolean) => resolverAjuste(tarefaId, ajuste.id, v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tarefa-ajustes", tarefaId] }),
    onError: (e: any) => alert(e?.response?.data?.error?.message || "Não foi possível atualizar o ajuste."),
  });

  // Marcar/desmarcar um item de checklist DENTRO do texto do ajuste já
  // registrado. Alterna o N-ésimo `- [ ]`/`- [x]` da descrição e salva.
  const editM = useMutation({
    mutationFn: (dados: { descricao?: string | null; motivoId?: string | null; categoria?: "interno" | "cliente" }) =>
      editarAjuste(tarefaId, ajuste.id, dados),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tarefa-ajustes", tarefaId] }); setEditando(false); },
    onError: (e: any) => alert(e?.response?.data?.error?.message || "Não foi possível atualizar o item."),
  });

  function salvarEdicao() {
    editM.mutate({
      descricao: rascDesc.trim() || null,
      motivoId: rascMotivo || null,
      categoria: rascCategoria,
    });
  }

  function alternarChecklist(idx: number) {
    let n = -1;
    const nova = (ajuste.descricao || "").split("\n").map((l) => {
      const m = /^- \[([ xX])\](.*)$/.exec(l);
      if (!m) return l;
      n++;
      if (n !== idx) return l;
      return `- [${m[1].toLowerCase() === "x" ? " " : "x"}]${m[2]}`;
    }).join("\n");
    // Só a descrição: mandar motivo/categoria aqui esbarraria no guard do
    // backend quando quem marca o item não é o autor do ajuste.
    editM.mutate({ descricao: nova });
  }

  async function remover() {
    const ok = await confirm({
      title: "Remover ajuste",
      message: "Remover este ajuste? Só é possível se ele ainda não tiver tempo registrado.",
      danger: true,
      confirmText: "Remover",
    });
    if (!ok) return;
    setRemovendo(true);
    try {
      await deletarAjuste(tarefaId, ajuste.id);
      qc.invalidateQueries({ queryKey: ["tarefa-ajustes", tarefaId] });
    } catch (e: any) {
      alert(e?.response?.data?.error?.message || "Não foi possível remover o ajuste.");
    } finally {
      setRemovendo(false);
    }
  }

  async function adicionar(files: FileList | null) {
    const escolhidos = Array.from(files || []);
    const validos = escolhidos.filter((f) => f.size <= 25 * 1024 * 1024);
    if (validos.length < escolhidos.length) alert("Alguns arquivos passam de 25 MB e foram ignorados.");
    if (validos.length === 0) return;
    setEnviando(true);
    const falhas: string[] = [];
    try {
      for (const f of validos) {
        try { await uploadAnexo(tarefaId, f, { ajusteId: ajuste.id }); } catch { falhas.push(f.name); }
      }
      qc.invalidateQueries({ queryKey: ["tarefa-ajustes", tarefaId] });
    } finally {
      setEnviando(false);
    }
    if (falhas.length) alert(`${falhas.length} anexo(s) não subiram: ${falhas.join(", ")}. Tente de novo.`);
  }

  return (
    <li
      className={
        "text-[11px] rounded-lg p-2 border transition-colors " +
        (resolvido
          ? "border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-300"
          : "border-slate-100 dark:border-slate-800 text-slate-500")
      }
    >
      <div className="flex items-center gap-1.5">
        {/* Check de "realizado" (pedido do Ricardo): confere o material e marca.
            Marcado = verde; desmarca reabre. */}
        <button
          type="button"
          onClick={() => resolverM.mutate(!resolvido)}
          disabled={resolverM.isPending}
          className={"shrink-0 " + (resolvido ? "text-emerald-600" : "text-slate-400 hover:text-emerald-600")}
          title={resolvido ? "Resolvido — clique pra reabrir" : "Marcar como resolvido"}
          aria-label={resolvido ? "Reabrir ajuste" : "Marcar ajuste como resolvido"}
        >
          {resolvido ? <CheckSquare size={15} /> : <Square size={15} />}
        </button>
        <span className={`w-2 h-2 rounded-full shrink-0 ${ajuste.categoria === "cliente" ? "bg-amber-500" : "bg-blue-500"}`} />
        <span className={"font-medium " + (resolvido ? "line-through opacity-80" : "text-slate-600 dark:text-slate-300")}>{ajuste.motivo?.label || "Sem motivo"}</span>
        <span className={resolvido ? "opacity-70" : "text-slate-400"}>· {ajuste.categoria === "cliente" ? "cliente" : "interno"}</span>
        {resolvido && <span className="ml-1 text-emerald-600 dark:text-emerald-400 font-semibold">✓ feito</span>}
        {podeEditar && !editando && (
          <button
            type="button"
            onClick={abrirEdicao}
            className="ml-auto text-slate-400 hover:text-blue-600 shrink-0"
            title="Editar este ajuste (texto, motivo e categoria)"
          >
            <Pencil size={12} />
          </button>
        )}
        {podeRemover && (
          <button
            type="button"
            onClick={remover}
            disabled={removendo}
            className={(podeEditar && !editando ? "" : "ml-auto ") + "text-slate-400 hover:text-red-500 shrink-0 disabled:opacity-50"}
            title="Remover ajuste (só se não tiver tempo registrado)"
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>

      {/* EDIÇÃO do ajuste já postado. Mesmo editor rico do registro, então dá pra
          corrigir o texto, somar itens de checklist e colar imagem no meio. */}
      {editando && (
        <div className="mt-1.5 space-y-1.5">
          <div className="flex gap-1.5">
            <select
              value={rascMotivo}
              onChange={(e) => setRascMotivo(e.target.value)}
              className="flex-1 min-w-0 text-[11px] px-1.5 py-1 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 outline-none"
            >
              <option value="">— sem motivo</option>
              {motivos.map((m: any) => (
                <option key={m.id} value={m.id}>{m.label}</option>
              ))}
              {/* Motivo que saiu do catálogo continua visível pra não sumir sozinho. */}
              {ajuste.motivoId && !motivos.some((m: any) => m.id === ajuste.motivoId) && (
                <option value={ajuste.motivoId}>{ajuste.motivo?.label || "motivo antigo"} (fora do catálogo)</option>
              )}
            </select>
            <select
              value={rascCategoria}
              onChange={(e) => setRascCategoria(e.target.value === "cliente" ? "cliente" : "interno")}
              className="text-[11px] px-1.5 py-1 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 outline-none"
            >
              <option value="interno">interno</option>
              <option value="cliente">cliente</option>
            </select>
          </div>
          <MentionTextarea
            value={rascDesc}
            onChange={setRascDesc}
            usuarios={usuarios}
            toolbar
            rows={3}
            placeholder="O que precisa ser ajustado…"
            onUploadImagem={(f) => uploadAnexo(tarefaId, f, { ajusteId: ajuste.id }).then((a) => anexoUrlAbsoluto(a.url))}
          />
          <div className="flex justify-end gap-1.5">
            <button
              type="button"
              onClick={() => setEditando(false)}
              className="text-[11px] px-2 py-1 rounded bg-slate-100 dark:bg-slate-700 hover:bg-slate-200"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={salvarEdicao}
              disabled={editM.isPending}
              className="text-[11px] px-2.5 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            >
              {editM.isPending ? "Salvando…" : "Salvar"}
            </button>
          </div>
        </div>
      )}
      {!editando && ajuste.descricao && (
        <div className="mt-1 whitespace-pre-wrap break-words leading-snug text-slate-600 dark:text-slate-300">
          {renderTextoRico(ajuste.descricao, "text-blue-600 hover:underline break-all", alternarChecklist)}
        </div>
      )}
      {ajuste.anexos && ajuste.anexos.length > 0 && (
        <div className="mt-1.5 space-y-1.5">
          {ajuste.anexos.map((a) => (
            <AnexoView key={a.id} anexo={a} tarefaId={tarefaId} />
          ))}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/*,video/*,audio/*,application/pdf"
        className="hidden"
        onChange={(e) => { adicionar(e.target.files); e.target.value = ""; }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={enviando}
        className="mt-1.5 text-[11px] text-blue-600 hover:text-blue-700 hover:underline disabled:opacity-50"
      >
        {enviando ? "Enviando…" : "+ arquivo"}
      </button>
    </li>
  );
}

/** Seção de ajustes da tarefa: lista os registrados + botão pra registrar novo. */
function SecaoAjustes({ tarefaId }: { tarefaId: string }) {
  const { data: ajustes = [] } = useQuery({
    queryKey: ["tarefa-ajustes", tarefaId],
    queryFn: () => listarAjustesTarefa(tarefaId),
  });
  // Mesma chave do resto do drawer → sai do cache, sem request extra. Serve pro
  // @menção do editor ao EDITAR um ajuste (igual o NovoAjusteInline faz).
  const { data: usuarios = [] } = useQuery({ queryKey: ["usuarios-ativos"], queryFn: listarUsuariosAtivos });

  // Todos resolvidos → a seção inteira fica verde (pedido do Ricardo: ficar
  // claro, de relance, que todos os ajustes já foram feitos).
  const todosResolvidos = ajustes.length > 0 && ajustes.every((a) => !!a.resolvidoEm);
  const feitos = ajustes.filter((a) => a.resolvidoEm).length;

  return (
    <div className="mt-2 border-t border-slate-100 dark:border-slate-800 pt-2">
      <div className="flex items-center justify-between">
        <span
          className={
            "text-xs font-medium inline-flex items-center gap-1 " +
            (todosResolvidos ? "text-emerald-600 dark:text-emerald-400" : "text-slate-600 dark:text-slate-300")
          }
        >
          {todosResolvidos ? <CheckCircle2 size={13} /> : <Wrench size={12} />}
          {todosResolvidos ? "Todos os ajustes resolvidos" : "Ajustes"}
          {ajustes.length > 0 && (
            <span className={todosResolvidos ? "text-emerald-500/80" : "text-slate-400"}>
              ({feitos}/{ajustes.length})
            </span>
          )}
        </span>
      </div>

      {ajustes.length > 0 && (
        <ul className="mt-1 space-y-1.5">
          {ajustes.map((a) => (
            <AjusteItem key={a.id} ajuste={a} tarefaId={tarefaId} usuarios={usuarios} />
          ))}
        </ul>
      )}

      {/* Campo FIXO pra registrar ajuste vendo os anteriores (pedido do Ricardo):
          antes era um modal que cobria tudo. Agora fica aqui embaixo, com editor
          rico (formatação sem código + colar imagem). */}
      <NovoAjusteInline tarefaId={tarefaId} />
    </div>
  );
}

/** Composer fixo de ajuste: categoria + motivo + editor rico + anexos. */
function NovoAjusteInline({ tarefaId }: { tarefaId: string }) {
  const qc = useQueryClient();
  const [categoria, setCategoria] = useState<"interno" | "cliente">("interno");
  const [motivoId, setMotivoId] = useState("");
  const [descricao, setDescricao] = useState("");
  const [arquivos, setArquivos] = useState<File[]>([]);
  const [salvando, setSalvando] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const { data: motivos = [] } = useQuery({ queryKey: ["motivos-ajuste", "ativos"], queryFn: () => listarMotivosAjuste() });
  const { data: usuarios = [] } = useQuery({ queryKey: ["usuarios-ativos"], queryFn: listarUsuariosAtivos });

  // Melhoria 11 — rascunho do ajuste em andamento (restaura ao reabrir; registrar limpa).
  useRascunhoCampo(`rascunho_ajuste_${tarefaId}`, descricao, setDescricao);

  // O MOTIVO é obrigatório (ver registrarAjuste no backend): sem ele o ajuste
  // vira "Sem motivo" no relatório e não dá pra agir em cima. Precisa também de
  // algum conteúdo — motivo sozinho não descreve o que tem que ser refeito.
  const temConteudo = descricao.trim().length > 0 || arquivos.length > 0;
  const podeRegistrar = !!motivoId && temConteudo;

  async function registrar() {
    if (!podeRegistrar) return;
    setSalvando(true);
    try {
      const { falhas } = await registrarAjusteComAnexos(
        tarefaId,
        { categoria, motivoId: motivoId || null, descricao: descricao.trim() || null },
        arquivos,
      );
      qc.invalidateQueries({ queryKey: ["tarefa-ajustes", tarefaId] });
      if (falhas.length) alert(`Ajuste registrado, mas ${falhas.length} anexo(s) não subiram: ${falhas.join(", ")}.`);
      // limpa pra o próximo (mantém a categoria — normalmente é a mesma na sequência)
      setMotivoId("");
      setDescricao("");
      setArquivos([]);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="mt-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-2 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex rounded-lg border border-slate-300 dark:border-slate-600 overflow-hidden text-[11px]">
          {([{ id: "interno", label: "Interno" }, { id: "cliente", label: "Cliente" }] as const).map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setCategoria(opt.id)}
              className={
                "px-2.5 py-1 font-medium transition-colors " +
                (categoria === opt.id ? "bg-blue-600 text-white" : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700")
              }
            >
              {opt.label}
            </button>
          ))}
        </div>
        <select
          value={motivoId}
          onChange={(e) => setMotivoId(e.target.value)}
          className="flex-1 min-w-[120px] px-2 py-1 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-[11px] outline-none focus:border-blue-500"
        >
          <option value="">Motivo…</option>
          {motivos.map((m) => (
            <option key={m.id} value={m.id}>{m.label}</option>
          ))}
        </select>
      </div>

      <MentionTextarea
        value={descricao}
        onChange={setDescricao}
        usuarios={usuarios}
        toolbar
        rows={2}
        placeholder="Detalhe o ajuste… (negrito/cor pela barra; cole com Ctrl+V ou arraste a imagem pra cá)"
        onUploadImagem={(file) => uploadAnexo(tarefaId, file).then((a) => anexoUrlAbsoluto(a.url))}
      />

      {arquivos.length > 0 && (
        <ul className="space-y-1">
          {arquivos.map((f, i) => (
            <li key={i} className="flex items-center justify-between gap-2 text-[11px] bg-white dark:bg-slate-800 rounded px-2 py-1">
              <span className="truncate">{f.name} <span className="text-slate-400">({(f.size / (1024 * 1024)).toFixed(1)} MB)</span></span>
              <button type="button" onClick={() => setArquivos((p) => p.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-500 shrink-0" title="Remover">
                <X size={11} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center justify-between gap-2">
        <input
          ref={fileRef}
          type="file"
          multiple
          accept="image/*,video/*,audio/*,application/pdf"
          className="hidden"
          onChange={(e) => {
            const escolhidos = Array.from(e.target.files || []);
            const validos = escolhidos.filter((f) => f.size <= 25 * 1024 * 1024);
            if (validos.length < escolhidos.length) alert("Alguns arquivos passam de 25 MB e foram ignorados.");
            if (validos.length) setArquivos((p) => [...p, ...validos]);
            e.target.value = "";
          }}
        />
        <button type="button" onClick={() => fileRef.current?.click()} className="text-[11px] text-slate-500 hover:text-blue-600 inline-flex items-center gap-1">
          <Paperclip size={12} /> anexar vídeo/PDF/áudio
        </button>
        {!motivoId && temConteudo && (
          <span className="text-[11px] text-amber-600">Escolha o motivo</span>
        )}
        <button
          type="button"
          onClick={registrar}
          disabled={salvando || !podeRegistrar}
          title={!motivoId ? "Escolha o motivo do ajuste" : undefined}
          className="px-3 py-1 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-medium disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {salvando ? "Registrando…" : "Registrar ajuste"}
        </button>
      </div>
    </div>
  );
}
